import { sortObjectsArrayByStringDesc } from '@/support/String';
import {cloneDeep} from "lodash";

export enum ReportStatus {
  PREPARATION = 'hin_preparation',
  WAITING_FOR_SIGNATURE = 'hin_waiting_for_signature',
  ASSIGMENT_ACCEPTED = 'hin_assignment_accepted',
  ASSIGMENT_STARTED = 'hin_assignment_started',
  ASSIGMENT_COMPLETED = 'hin_assignment_completed',
  EVALUATION_UPLOADED = 'hin_evaluation_uploaded',
  CLOSED = 'hin_closed',
  ABORTED = 'hin_aborted',
  MIGRATED = 'hin_migrated',
}

export const reportStatusMap: ReportStatusMap[] = [
  { key: ReportStatus.PREPARATION, label: 'Voorbereiding', color: 'blue-magenta', roles: ['contractor', 'bureau'] },
  { key: ReportStatus.WAITING_FOR_SIGNATURE, label: 'Wachten op ondertekening', color: 'blue', roles: ['contractor', 'bureau'] },
  { key: ReportStatus.ASSIGMENT_ACCEPTED, label: 'Opdracht aangenomen', color: 'magenta', roles: ['contractor', 'bureau'] },
  { key: ReportStatus.ASSIGMENT_STARTED, label: 'Werkzaamheden gestart', color: 'orange', roles: ['contractor', 'bureau'] },
  { key: ReportStatus.ASSIGMENT_COMPLETED, label: 'Werkzaamheden gereed', color: 'green-cyan', roles: ['contractor', 'bureau'] },
  { key: ReportStatus.EVALUATION_UPLOADED, label: 'Prestatieverklaring afgegeven', color: 'pink', roles: ['contractor', 'bureau'] },
  { key: ReportStatus.CLOSED, label: 'Afgerond', color: 'green', roles: ['contractor', 'bureau'] },
  { key: ReportStatus.ABORTED, label: 'Afgebroken', color: 'red', roles: ['contractor', 'bureau'] },
  { key: ReportStatus.MIGRATED, label: 'Gemigreerd naar Cases', color: 'dark-blue', roles: ['contractor', 'bureau'] },
];

export const statusHexColor: {[key: string] : string} = {
  red: '#F74949',
  'red-orange': '#ce5d25',
  green: '#62DC4F',
  'green-cyan': '#2edba7',
  brown: '#852222',
  orange: '#f98d35',
  yellow: '#F9BD35',
  pink: '#ff0077',
  magenta: '#a12891',
  'pink-magenta': '#ff66cc',
  'blue-magenta': '#551A8B',
  blue: '#B8DBEB',
  'secondary-dark': '#837f16',
  'primary-dark': '#354454',
};

/**
 * This returns the color based on the key from reportStatusMap
 * @param color
 */
export const getStatusColor = (key: string): string => (getStatusAttribute(key, 'color') as string);

/**
 * This returns the label based on the key from reportStatusMap
 * @param label
 */
export const getStatusLabel = (key: string): string => (getStatusAttribute(key, 'label') as string);

/**
 * This returns the roles based on the key from reportStatusMap
 * @param roles
 */
export const getStatusRoles = (key: string): string[] => (getStatusAttribute(key, 'roles') as string[]);

/**
 * This returns a sorted array from reportStatusMap
 * @param sorted
 */
export const getSortedStatus = (key: string): ReportStatusMap[] => sortObjectsArrayByStringDesc(cloneDeep(reportStatusMap), key);

/**
 * This returns a array of status keys from reportStatusMap
 * @param keys
 */
export const getStatusKeys = (): string[] => reportStatusMap.map((reportStatus: ReportStatusMap) => reportStatus.key);

/**
 *  This replaces status key with a styled/colored label
 * @param message
 */
export const getStyledStatusLog = (message: string, type = '') => {
  let parsed = message;
  let count = 0;

  if (type === 'status_change') {
    getSortedStatus('key').forEach((status: ReportStatusMap) => {
      if (message.includes(status.key)) {
        if (count > 0) {
          return;
        }

        count = 1;
        parsed = message.replace(status.key, `<span class="font-weight-bold statusColor--${getStatusColor(status.key)}">${status.label}</span>`);
      }
    });
  }

  return parsed;
};

/**
 *  Gets specific status object from a string or string array values as a status array
 * @param message
 */
export const getStatusObjects = (key: string | string[]) => {
  const foundReportStatuses: ReportStatusMap[] = [];

  if (Array.isArray(key)) {
    (key as string[]).forEach((currentKey: string) => {
      foundReportStatuses.push(searchStatus(currentKey));
    });
  } else {
    foundReportStatuses.push(searchStatus(key));
  }

  return foundReportStatuses;
};

/**
 * This gets the specific object from reportStatusMap based on which key
 * @param attribute
 */
const getStatusAttribute = (key: string, attribute: string) => {
  const statusObject = searchStatus(key);

  return (statusObject as ReportStatusMap)[attribute];
};

/**
 * This seeks the specific object from reportStatusMap based on which key
 * @param search
 */
const searchStatus = (key: string) => {
  const defaultStatus: ReportStatusMap = { key: '', label: 'Onbekend', color: 'primary', roles: ['contractor', 'bureau'] };
  const status = reportStatusMap.find((reportStatus: ReportStatusMap) => reportStatus.key === key);

  return status || defaultStatus;
};

export const excludeReportStatuses = (key: string | string[]): ReportStatusMap[] => {
  let foundReportStatuses: ReportStatusMap[] = [];

  if (Array.isArray(key)) {
    foundReportStatuses = [...reportStatusMap, ...[]];
    (key as string[]).forEach((currentKey: string) => {
      foundReportStatuses = foundReportStatuses.filter((reportType: ReportStatusMap) => reportType.key !== currentKey);
    });
  } else {
    foundReportStatuses = reportStatusMap.filter((reportType: ReportStatusMap) => reportType.key !== key);
  }

  return foundReportStatuses;
};

export interface ReportStatusMap {
  [key: string]: string | string[];
  key: string;
  label: string;
  color: string;
  roles: string[];
}
